<template>
  <page-template :is-loading="isLoading" :data-exists="globalFilters !== null">
    <template #pageHeadline>
      {{ $t('globalFilter.listPage.pageHeadline') }}
    </template>
    <template #pageContent>
      <rbz-table
        :columns="columns"
        :table-data="globalFilters"
        :sticky-header="true"
        class="rbz-list-table"
        data-qa="global-filters-table"
        :row-buttons="rowButtons"
        :new-button-link="getNewGlobalFilterPath()"
        :show-filter-row="true"
        @duplicate-row-button-clicked="duplicateGlobalFilter"
        @delete-row-button-clicked="openDeleteGlobalFilterModal"
        @download-data="downloadDoc"
      >
        <template #tagsUseCell="cellProps">
          <div
            class="tags-use-cell"
            v-if="cellProps.row.tags.length > 0"
            @mouseenter="checkTagTooltip"
          >
            <span
              v-if="cellProps.row.tags.length > 1"
              class="show-more-wrapper"
              @click.stop.prevent="showTagNames(cellProps.row)"
            >
              <span class="tag-name ellipsis no-tooltip" v-b-tooltip="cellProps.row.tags[0]">{{
                cellProps.row.tags[0]
              }}</span>
              <a href="#" class="show-more">show {{ cellProps.row.tags.length - 1 }} more</a>
            </span>
            <span v-else class="tag-name ellipsis no-tooltip" v-b-tooltip="cellProps.row.tags[0]">{{
              cellProps.row.tags[0]
            }}</span>
          </div>
        </template>
      </rbz-table>
      <git-history
        :api-path="gitAPIPath"
        ref="gitHistory"
        :restore-target-title="`[${entityName.plural}]`"
        @restore-version="loadGlobalFilters"
      />
      <rbz-modal-dialog
        :show="showDeleteGlobalFilterModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteGlobalFilter"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteGlobalFilterModal = false"
        @closeModal="showDeleteGlobalFilterModal = false"
        size="medium"
      >
        <template #content>
          {{ $t('common.operations.delete.modal.message', { name: selectedGlobalFilter.name }) }}
        </template>
      </rbz-modal-dialog>

      <rbz-offcanvas v-model:showOffcanvas="showTagNamesModal">
        <template #header>
          {{ $t('globalFilter.listPage.tags') }}
        </template>
        <template #content>
          <div class="tag-names-table-wrapper">
            <rbz-table
              :columns="tagNamesColumns"
              class="tag-names-table"
              :show-new-button="false"
              :table-data="tagNames"
              rows-per-page="all"
              :show-download-button="false"
              :show-filter-row="true"
              :hide-header-row="true"
              :show-narrow-column="false"
              :show-top-navbar="true"
              :show-buttons-column="false"
            >
            </rbz-table>
          </div>
        </template>
        <template #footer>
          <rbz-form-button
            class="rbz-offcanvas-footer-button"
            :label="$t('common.operations.cancel.label')"
            @click="showTagNamesModal = false"
            variant="secondary"
          />
          <rbz-form-button
            class="rbz-offcanvas-footer-button"
            :label="$t('globalFilter.listPage.editGlobalFilter')"
            @click="editGlobalFilter(tagsGlobalFilter)"
            variant="primary"
          />
        </template>
      </rbz-offcanvas>
    </template>
  </page-template>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import {
  Action,
  DocumentName,
  GlobalFilter,
  GlobalFilterRuleSection,
  TrustedSource,
  ApiTrustedSource,
  ApiPlanet,
} from '@/types';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RequestsUtils from '@/helpers/RequestsUtils';
import Utils from 'ui-component-library/helpers/Utils';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import PageTemplate from '@/views/PageTemplate.vue';
import _ from 'lodash';
import GitHistory from '@/components/GitHistory.vue';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import {
  RbzTableColumnOptions,
  RbzTableRowButton,
  GenericObject,
} from 'ui-component-library/rbz/types';
import RbzOffcanvas from '@/components/RbzOffcanvas.vue';
import RbzFormButton from '@/components/RbzFormButton.vue';
import { useRouting } from '@/helpers/RoutingUtils';

export default defineComponent({
  name: 'GlobalFiltersList',

  setup() {
    const { getEditPath, getNewPath, navigateTo } = useRouting();
    return {
      getEditPath,
      getNewPath,
      navigateTo,
    };
  },

  components: {
    GitHistory,
    PageTemplate,
    RbzModalDialog,
    RbzTable,
    RbzOffcanvas,
    RbzFormButton,
  },

  data() {
    return {
      columns: [
        {
          id: 'name',
          title: this.$t('globalFilter.listPage.name'),
          fieldNames: ['name'],
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'ellipsis',
          classes: 'clickable',
          autoFocus: true,
          linkCallback: (item) => {
            return this.getGlobalFilterPath(item);
          },
        },
        {
          id: 'active',
          title: this.$t('globalFilter.listPage.active'),
          fieldNames: ['active'],
          displayFunction: (item: GlobalFilter) => {
            return item.active ? this.$t('common.yes') : this.$t('common.no');
          },
          isSortable: true,
          isSearchable: true,
          classes: 'width-100px',
        },
        {
          id: 'action',
          title: this.$t('globalFilter.listPage.action'),
          fieldNames: ['action'],
          isSortable: true,
          displayFunction: (item: GlobalFilter) => {
            const action = _.find(this.actions, (action) => {
              return action.id === item.action;
            });
            return action?.name || '';
          },
          isSearchable: true,
          classes: 'width-200px action-cell',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'system-managed',
          title: this.$t('globalFilter.listPage.systemManaged'),
          fieldNames: ['id'],
          isSortable: true,
          displayFunction: (item: GlobalFilter) => {
            return item.id.startsWith('waap-') ? 'Yes' : 'No';
          },
          isSearchable: true,
          classes: 'width-150px',
        },
        {
          id: 'number-of-sections',
          title: this.$t('globalFilter.listPage.numOfSections'),
          fieldNames: ['rule'],
          isSortable: true,
          isNumber: true,
          displayFunction: (item: GlobalFilter) => {
            const counter = this.countEntriesAndSections(item.rule);
            return counter.sections;
          },
          isSearchable: true,
          classes: 'width-150px',
        },
        {
          id: 'number-of-entries',
          title: this.$t('globalFilter.listPage.numOfEntries'),
          fieldNames: ['rule'],
          isSortable: true,
          displayFunction: (item: GlobalFilter) => {
            const counter = this.countEntriesAndSections(item.rule);
            return counter.entries;
          },
          isSearchable: true,
          isNumber: true,
          classes: 'width-150px',
        },
        {
          id: 'tags',
          title: this.$t('globalFilter.listPage.tags'),
          fieldNames: ['tags'],
          isSortable: true,
          displayFunction: (item: GlobalFilter) => {
            return item.tags.join('\n');
          },
          isSearchable: true,
          classes: 'width-260px',
          displaySlotName: 'tagsUseCell',
        },
      ] as RbzTableColumnOptions[],
      rowButtons: [
        {
          id: 'edit',
          tooltip: this.$t('common.operations.edit.tableTooltip'),
          icon: 'IconEdit',
          linkCallback: this.getGlobalFilterPath,
        },
        {
          id: 'duplicate',
          tooltip: this.$t('common.operations.duplicate.tableTooltip'),
          icon: 'IconDuplicate',
        },
        {
          id: 'delete',
          icon: 'IconDelete',
          disabledCallback: this.isDeleteDisabled,
          tooltipCallback: this.deleteTooltip,
        },
      ] as RbzTableRowButton[],
      globalFilters: null as GlobalFilter[],
      actions: [] as Action[],
      pageLoadingCounter: 0,

      showDeleteGlobalFilterModal: false,
      selectedGlobalFilter: null as GlobalFilter,
      trustedSourcesData: [] as TrustedSource[],
      showTagNamesModal: false,
      tagNames: [] as { name: string }[],
      tagsGlobalFilter: null as GlobalFilter,
      planet: null as ApiPlanet,
    };
  },

  watch: {
    selectedBranch: {
      handler: async function (val, oldVal) {
        if ((this.$route.name as string).includes('Global Filters List') && val && val !== oldVal) {
          this.setPageLoadingStatus(true);
          try {
            await Promise.all([
              this.loadGlobalFilters(),
              this.loadActions(),
              this.loadTrustedSources(),
            ]);
          } finally {
            this.setPageLoadingStatus(false);
          }
        }
      },
      immediate: true,
    },
  },

  computed: {
    selectedBranch(): string {
      if (!this.branchesStore.selectedBranchId) {
        this.branchesStore.setSelectedBranch(this.branchesStore.defaultBranch);
      }
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useUserStore),

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    entityName(): DocumentName {
      return {
        plural: this.$t('globalFilter.plural'),
        singular: this.$t('globalFilter.singular'),
      };
    },

    isLoading(): boolean {
      return this.pageLoadingCounter === null || this.pageLoadingCounter > 0;
    },

    gitAPIPath(): string {
      return `conf/${this.selectedBranch}/global-filters/versions`;
    },

    tagNamesColumns(): RbzTableColumnOptions[] {
      return [
        {
          id: 'tag_names',
          title: this.$t('globalFilter.listPage.tags'),
          fieldNames: ['name'],
          isSearchable: true,
          cellContentClasses: 'ellipsis',
        },
      ];
    },
  },

  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    async loadGlobalFilters() {
      this.setPageLoadingStatus(true);
      const url = `conf/${this.selectedBranch}/global-filters`;
      const fieldNames = _.flatMap(this.columns, 'fieldNames');
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url,
        config: { headers: { 'x-fields': `id, ${_.uniq(fieldNames).join(', ')}` } },
      });
      this.globalFilters = response?.data?.items || [];
      this.globalFilters.forEach((item) => {
        item.tags.sort((a, b) => a.localeCompare(b));
      });
      this.setPageLoadingStatus(false);
    },

    async loadActions() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/actions`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.actions = response?.data?.items || [];
      this.setPageLoadingStatus(false);
    },

    isDeleteDisabled(globalFilter: GlobalFilter) {
      return (
        this.isGlobalFilterProtected(globalFilter) || this.isGlobalFilterReferenced(globalFilter)
      );
    },

    isGlobalFilterProtected(globalFilter: GlobalFilter): boolean {
      return globalFilter.id.startsWith('waap-');
    },

    isGlobalFilterReferenced(globalFilter: GlobalFilter): boolean {
      return this.trustedSourcesData.some(
        (trusted_net) =>
          trusted_net.source === 'global_filter' && trusted_net.globalFilter === globalFilter.id
      );
    },

    deleteTooltip(globalFilter: GlobalFilter): string {
      if (this.isGlobalFilterProtected(globalFilter)) {
        return this.$t('common.operations.delete.disabledTooltip.protected', this.entityName);
      }
      if (this.isGlobalFilterReferenced(globalFilter)) {
        return this.$t('globalFilter.configPage.deleteDisabledTooltip.referencedTrustedSource');
      }
      return this.$t('common.operations.delete.tableTooltip');
    },

    getGlobalFilterPath(globalFilter: GlobalFilter) {
      return this.getEditPath('global-filters', globalFilter.id);
    },

    getNewGlobalFilterPath() {
      return this.getNewPath('global-filters');
    },

    editGlobalFilter(globalFilter: GlobalFilter) {
      this.navigateTo(this.getGlobalFilterPath(globalFilter));
    },

    mapGenericObjectToGlobalFilter(data: GenericObject): GlobalFilter {
      const globalFilter: GlobalFilter = {
        id: (data.id as string) ?? '',
        name: (data.name as string) ?? '',
        source: (data.source as string) ?? '',
        mdate: (data.mdate as string) ?? '',
        description: (data.description as string) ?? '',
        active: (data.active as boolean) ?? true,
        tags: (data.tags as string[]) ?? [],
        action: (data.action as string) ?? '',
        rule: (data.rule as GlobalFilter['rule']) ?? { relation: 'OR', entries: [] },
      };
      return globalFilter;
    },

    async duplicateGlobalFilter(data: GenericObject) {
      const globalFilter = this.mapGenericObjectToGlobalFilter(data);
      this.setPageLoadingStatus(true);
      const getOriginalResponse = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/global-filters/${globalFilter.id}`,
      });
      const successMessage = this.$t(
        'common.operations.duplicate.messages.success',
        this.entityName
      );
      const failureMessage = this.$t(
        'common.operations.duplicate.messages.failure',
        this.entityName
      );
      if (!getOriginalResponse?.data) {
        Utils.toast({
          message: failureMessage,
          type: 'is-danger',
        });
        return;
      }
      const originalGlobalFilter = getOriginalResponse.data;
      const globalFilterToAdd = _.cloneDeep(originalGlobalFilter);
      globalFilterToAdd.id = DatasetsUtils.generateUUID2();
      globalFilterToAdd.name = Utils.generateUniqueEntityName(
        globalFilterToAdd.name,
        _.map(this.globalFilters, 'name'),
        true
      );
      const url = `conf/${this.selectedBranch}/global-filters/${globalFilterToAdd.id}`;
      const postNewResponse = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'POST',
        url,
        data: globalFilterToAdd,
        successMessage,
        failureMessage,
      });
      if (postNewResponse) {
        this.editGlobalFilter(globalFilterToAdd);
      }
      this.setPageLoadingStatus(false);
    },

    async deleteGlobalFilter() {
      this.setPageLoadingStatus(true);
      const globalFilterId = this.selectedGlobalFilter.id;
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: `conf/${this.selectedBranch}/global-filters/${globalFilterId}`,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.globalFilters = _.filter(this.globalFilters, (globalFilter) => {
          return globalFilter.id !== globalFilterId;
        });
      }
      this.showDeleteGlobalFilterModal = false;
      this.setPageLoadingStatus(false);
    },

    async loadTrustedSources() {
      const url = `conf/${this.selectedBranch}/planets`;
      const methodName = 'GET';
      const response = await RequestsUtils.sendRequest({ methodName, url });
      const data = response?.data || {};
      this.planet = data;
      this.trustedSourcesData = _.map(data.trusted_nets, (trusted: ApiTrustedSource) => {
        return {
          id: DatasetsUtils.generateUUID2(),
          source: trusted.source,
          address: trusted.address,
          globalFilter: trusted.gf_id,
          comment: trusted.comment,
        };
      });
    },

    downloadDoc() {
      Utils.downloadFile('global-filters', 'json', this.globalFilters);
    },

    openDeleteGlobalFilterModal(data: GenericObject) {
      const globalFilter = this.mapGenericObjectToGlobalFilter(data);
      this.showDeleteGlobalFilterModal = true;
      this.selectedGlobalFilter = globalFilter;
    },

    countEntriesAndSections(rule: GlobalFilterRuleSection, counters = { entries: 0, sections: 0 }) {
      if (Array.isArray(rule)) {
        counters.entries++;
      } else if (Array.isArray(rule?.entries)) {
        for (const entry of rule.entries) {
          if (Array.isArray(entry)) {
            counters.entries++;
          } else {
            counters.sections++;
            this.countEntriesAndSections(entry, counters);
          }
        }
      }
      return counters;
    },
    showTagNames(data: GenericObject) {
      const globalFilter = this.mapGenericObjectToGlobalFilter(data);
      this.showTagNamesModal = true;
      this.tagsGlobalFilter = globalFilter;
      this.tagNames = globalFilter.tags.map((tag: string) => ({
        name: tag,
      }));
    },

    checkTagTooltip(event: MouseEvent) {
      const target = event.currentTarget as HTMLElement | null;
      if (!target) {
        return;
      }

      const el = target.querySelector('.tag-name') as HTMLElement | null;
      if (!el) {
        return;
      }
      if (el.scrollWidth > el.clientWidth) {
        el.classList.remove('no-tooltip');
      } else {
        el.classList.add('no-tooltip');
      }
    },
  },
});
</script>

<style scoped lang="scss">
.show-more-wrapper {
  display: flex;
  justify-content: flex-start;
  width: 100%;
  align-items: center;

  .show-more {
    white-space: nowrap;
    margin-left: 5px;
  }

  .tag-name {
    width: 55%;
  }
}

.tag-name {
  display: block;
  cursor: default;
}

.rbz-offcanvas-footer-button {
  padding: 10px 0 10px 10px;
}

.tag-names-table-wrapper :deep(.tag-names-table) {
  td {
    border-bottom: 0;
  }

  .filter-cell {
    padding: 16px 0;
    width: 100%;
  }

  .data-row:hover {
    background-color: transparent;
  }

  .data-row .row-buttons {
    opacity: 0;
  }

  .data-cell {
    padding: 0;
    height: 0;
  }

  .data-cell .data-cell-content {
    padding: 0;
  }

  .filter-table-data-button .rbz-button {
    height: 36px;
    padding: 6px;
  }

  .table-results-wrapper {
    padding: 8px 12px;
  }

  .table-buttons {
    margin: 0 16px;
  }

  .table-buttons-icons {
    gap: 0;
  }
}

.no-tooltip {
  pointer-events: none;
}
</style>
